import { Center, Text, VStack } from '@chakra-ui/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAppToast } from '../../hooks/ui/useAppToast';
import { authService } from '../../services/auth.service';
import { describeAcceptRefusal } from '../../utils/invitationAcceptFailure';
import {
  clearInvitedWorkspace,
  clearPendingInvitationToken,
  readPendingInvitationToken,
  recordInvitedWorkspace,
} from '../../utils/pendingInvitation';
import { isWorkspaceSwitchSupersededError } from '../../utils/workspaceSwitch';
import { BowerNestLoader } from '../ui/BowerNestLoader';

// Component to handle auth page with invitation for logged-in users (Scenario 3)
export function AuthRedirect() {
  const navigate = useNavigate();
  const toast = useAppToast();
  const { user } = useAuth();
  const { switchWorkspace } = useWorkspace();
  const [isProcessing, setIsProcessing] = useState(true);
  const invitationGenerationRef = useRef(0);
  const authIdentity = user?.internalId || user?.uid || null;
  // The marker's owner is the Firebase uid, like WorkspaceContext's pin:
  // `authIdentity` flips between internalId and uid for the same person.
  const markerOwner = user?.uid ?? null;

  useLayoutEffect(
    () => () => {
      invitationGenerationRef.current += 1;
    },
    [authIdentity],
  );

  useEffect(() => {
    const generation = invitationGenerationRef.current + 1;
    invitationGenerationRef.current = generation;
    let cancelled = false;
    const isCurrent = () => !cancelled && invitationGenerationRef.current === generation;
    const cancelledContinuation = {};
    const ensureCurrent = () => {
      if (!isCurrent()) {
        throw cancelledContinuation;
      }
    };

    const handleInvitation = async () => {
      const invitationToken = readPendingInvitationToken();

      if (invitationToken) {
        // Logged-in user with invitation — accept it directly. Server
        // enforces strict email match, so a stale token from a
        // different account just gets a polite EMAIL_MISMATCH.
        try {
          const validation = await authService.validateInvitation(invitationToken);
          ensureCurrent();

          if (validation.isValid) {
            const result = await authService.acceptInvitation(invitationToken);
            // Record BEFORE the currency check. The membership now exists
            // server-side even if this run was just cancelled, and a restarted
            // run cannot re-accept a used token; the marker is what lets
            // usePendingInvitedWorkspaceSwitch still open the workspace.
            if (result.success && result.workspaceId && markerOwner) {
              recordInvitedWorkspace(result.workspaceId, markerOwner);
            }
            ensureCurrent();

            if (result.success) {
              ensureCurrent();
              clearPendingInvitationToken();

              // Invitation Accepted is captured server-side from
              // invitation.service.ts:acceptInvitation. The repository's
              // markAsUsed enforces "only mark if not yet used" so the
              // event fires exactly once per invitation.
              ensureCurrent();
              toast({
                title: '🎉 Welcome to Bower!',
                description: result.workspaceId
                  ? result.message || 'You have been successfully added to the workspace!'
                  : result.message || 'Your invitation has been accepted!',
                status: 'success',
                duration: 5000,
                isClosable: true,
              });

              if (result.workspaceId) {
                // Switch the active workspace BEFORE navigating so the
                // dashboard loads in the workspace they just joined,
                // not whichever one was active before.
                try {
                  await switchWorkspace(result.workspaceId);
                  clearInvitedWorkspace();
                  ensureCurrent();
                } catch (switchErr) {
                  ensureCurrent();
                  if (isWorkspaceSwitchSupersededError(switchErr)) {
                    ensureCurrent();
                    setIsProcessing(false);
                    navigate(ROUTES.dashboard, { replace: true });
                    return;
                  }
                  ensureCurrent();
                  console.warn('AuthRedirect: switchWorkspace failed', switchErr);
                }
                ensureCurrent();
                navigate(ROUTES.dashboard, { replace: true });
              } else {
                ensureCurrent();
                navigate(ROUTES.dashboard, { replace: true });
              }
            } else {
              ensureCurrent();
              console.error('🎫 AuthRedirect: Failed to accept invitation', {
                error: result.error,
              });
              ensureCurrent();
              // The silent dashboard landing here was the 2026-08-27 dead end;
              // `describeAcceptRefusal` decides copy + token retryability.
              const refusal = describeAcceptRefusal(result);
              if (!refusal.keepToken) clearPendingInvitationToken();
              toast(refusal.toast);
              ensureCurrent();
              navigate(ROUTES.dashboard, {
                state: { error: result.error || 'Failed to accept invitation' },
                replace: true,
              });
            }
          } else {
            ensureCurrent();
            console.warn('🎫 AuthRedirect: Invalid invitation', { reason: validation.reason });
            ensureCurrent();
            clearPendingInvitationToken();
            ensureCurrent();
            navigate(ROUTES.dashboard, {
              state: { error: validation.reason || 'Invalid invitation link' },
              replace: true,
            });
          }
        } catch (error) {
          if (error === cancelledContinuation) return;
          ensureCurrent();
          console.error('🎫 AuthRedirect: Failed to process invitation:', error);
          ensureCurrent();
          clearPendingInvitationToken();
          ensureCurrent();
          navigate(ROUTES.dashboard, {
            state: { error: 'Failed to process invitation' },
            replace: true,
          });
        }
      } else {
        ensureCurrent();
        // No invitation, just redirect to dashboard
        navigate(ROUTES.dashboard, { replace: true });
      }

      ensureCurrent();
      setIsProcessing(false);
    };

    handleInvitation();

    return () => {
      cancelled = true;
      if (invitationGenerationRef.current === generation) {
        invitationGenerationRef.current += 1;
      }
    };
  }, [authIdentity, markerOwner, navigate, toast, switchWorkspace]);

  if (!isProcessing) {
    return null;
  }

  return (
    <Center height="100vh">
      <VStack spacing={4}>
        <BowerNestLoader size="lg" />
        <Text color="text.secondary">Processing invitation...</Text>
      </VStack>
    </Center>
  );
}
